import './QuarterDetails.scss';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Popper,
  Checkbox,
  Chip,
  FormGroup,
  FormControlLabel,
  Typography,
  Divider
} from '@mui/material';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { RoadmapRelease, RoadmapStatus } from '../roadmap';
import {
  normalize,
  statusKey,
  removePTags,
  formatLongDate,
  bookingFlowKey,
  getDrupalImageUrl
} from '../roadmapFallbackData';
import {ReactComponent as CheckIcon} from '../../../assets/images/roadMap/checkIcon.svg';
import {ReactComponent as InprogressIcon} from '../../../assets/images/roadMap/inProgressIcon.svg';
import {ReactComponent as PlannedIcon} from '../../../assets/images/roadMap/planned.svg';
import {ReactComponent as PrimeBooking} from '../../../assets/images/roadMap/primeBooking.svg';
import {ReactComponent as PostBooking} from '../../../assets/images/roadMap/postBooking.svg';

interface QuarterDetailsProps {
  year: string;
  quarter: string;
}

const resolveEnvHref = (href: string | null) => {
  if (!href) return href;
  if (typeof window === 'undefined') return href;
  return href.replace(
    /^https?:\/\/ndc(-[a-z]+)?\.aircanada\.com/i,
    window.location.origin
  );
};

// Parse a title that may be wrapped in an <a href="...">...</a> tag into its
// link target and plain text (avoids dangerouslySetInnerHTML).
const parseTitle = (html: string) => {
  const doc = new DOMParser().parseFromString(html,'text/html');
  const anchor = doc.querySelector('a');
  if(!anchor) {
    return {
      text: doc.body.textContent?.trim() || '',
      href: null,
    };
  }
  return {
    text: anchor.textContent || '',
    href: resolveEnvHref(anchor.getAttribute('href')),
  };
};

const emptyFilters = (options): Record<string, string[]> =>
  Object.keys(options || {}).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {} as Record<string, string[]>);

// Accessible "Filter by" dropdown, modelled on the KnownIssuesFilterDropdown
// (ModalForm.tsx). Options are driven entirely by the `dropdown` object.
const FilterDropdown = ({
  options,
  headers,
  selectedFilters,
  setSelectedFilters,
  data
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Localized display label for a filter option. Booking flow options carry
  // English values (used for matching against release data) but display in the
  // active locale (e.g. "RÉSERVATION PRINCIPALE" / "APRÈS RÉSERVATION" in FR).
  const getOptionLabel = (category, option) => {
    if (category === 'bookingflow') {
      return normalize(option).includes('prime')
        ? data?.primeBooking || option
        : data?.postBooking || option;
    }
    if (category === 'status') {
      return data?.statusLabels?.[statusKey(option)] || option;
    }
    return option;
  };
  const [tempFilters, setTempFilters] = useState(selectedFilters);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);

  // Grow the page's main container only by however much the popup's bottom
  // edge falls past the container's bottom edge, so it has room to sit
  // fully inside the container instead of spilling onto the footer.
  useEffect(() => {
    const container = document.querySelector(
      '.quarterDetails'
    ) as HTMLElement | null;
    if (!container) return undefined;
    if (!open) {
      container.style.marginBottom = '';
      return undefined;
    }
    // Wait a tick for Popper to finish positioning the popup before measuring.
    const timeout = setTimeout(() => {
      // Reset first so the measurement isn't skewed by a margin left over
      // from a previous open.
      container.style.marginBottom = '';
      const paperRect = paperRef.current?.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const shortfall = paperRect ? paperRect.bottom - containerRect.bottom : 0;
      container.style.marginBottom = shortfall > 0 ? `${shortfall + 10}px` : '';
    }, 0);
    return () => {
      clearTimeout(timeout);
      container.style.marginBottom = '';
    };
  }, [open]);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const categories = Object.keys(options);

  const appliedCount = categories.reduce(
    (sum, cat) => sum + (selectedFilters[cat]?.length || 0),
    0
  );

  const tempCount = categories.reduce(
    (sum, cat) => sum + (tempFilters[cat]?.length || 0),
    0
  );

  // Keep actions disabled only in the pristine empty state
  // (first load or after an explicit reset).
  const disableActions = tempCount === 0 && appliedCount === 0;

  // Keep the working copy in sync with committed filters.
  useEffect(() => {
    setTempFilters(selectedFilters);
  }, [selectedFilters]);

  // On open: move focus to the first element (the dialog heading) once, and
  // leave it there until the user moves it themselves.
  useEffect(() => {
    if (!open) return undefined;
    const timeout = setTimeout(() => {
      headingRef.current?.focus();
    }, 50);
    return () => clearTimeout(timeout);
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setTempFilters(selectedFilters);
    triggerRef.current?.focus();
  };

  const handleToggle = (event) => {
    if (open) {
      handleClose();
    } else {
      setAnchorEl(event.currentTarget);
      setTempFilters(selectedFilters);
      setOpen(true);
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      handleClose();
    }
  };

  const handleToggleOption = (category, option) => {
    setTempFilters((prev) => {
      const current = prev[category] || [];
      const updated = current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option];
      return { ...prev, [category]: updated };
    });
  };

  const handleReset = () => {
    const cleared = emptyFilters(options);
    setTempFilters(cleared);
    setSelectedFilters(cleared);
    triggerRef.current?.focus();
  };

  const handleApply = () => {
    setSelectedFilters(tempFilters);
    setOpen(false);
    setAnchorEl(null);
    triggerRef.current?.focus();
  };

  const handlePaperKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        className="quarterDetails__filterBtn"
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={open}
        startIcon={<FilterListIcon fontSize="small" />}
        endIcon={
          open ? (
            <img src='/images/arrows/arrow-sort-down.svg' alt="" style={{rotate: '180deg', height: '16px', width: '16px', marginLeft: '-8px', paddingLeft: 0,justifyContent: 'end'}} />
          ) : (
            <img src='/images/arrows/arrow-sort-down.svg' alt="" style={{height: '16px', width: '16px', marginLeft: '-8px', paddingLeft: 0, justifyContent: 'end'}}/>
          )
        }
        // disableRipple
      >
        {data.filterBy}
        {appliedCount ? ` (${appliedCount})` : ''}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 3 }}
        // 'fixed' strategy avoids mispositioning (opening above the button)
        // when an ancestor has a CSS transform.
        // popperOptions={{ strategy: 'fixed' }}
        modifiers={[
          {
            // Real gap below the button so the popup never overlaps it.
            name: 'offset',
            options: { offset: [0, 8] }
          },
          {
            // Always keep the popup below the button; never flip above it.
            name: 'flip',
            enabled: false,
            options: { altAxis: false, tether: false },
          },
          {
            // Disable overflow shifting so the popup stays exactly at its
            // bottom-end placement and can never be pushed up over the button;
            // space is reserved on the scroll container instead (open effect).
            name: 'preventOverflow',
            enabled: true
          }
        ]}
      >
        <TrapFocus open={open} disableAutoFocus disableEnforceFocus>
          <Paper
            ref={paperRef}
            tabIndex={-1}
            onKeyDown={handlePaperKeyDown}
            sx={{
              // On mobile the filter button is full width, so match the popup
              // to the same content width (body has 13px side padding).
              minWidth: { xs: 'auto', sm: '261px' },
              maxWidth: { xs: 'none', sm: '261px' },
              width: { xs: 'calc(100vw - 26px)', sm: 'auto' },
              boxSizing: 'border-box',
              // !important guards against global ".MuiPaper-root" overrides
              // elsewhere in the app (e.g. AppSelect.scss) shrinking this popup.
              height: '500px !important',
              minHeight: '500px !important',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: '12px',
              boxShadow: '0px 0px 15px rgba(0,0,0,0.15)'
            }}
          >
            {/* Fixed header: first heading + close button */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: '16px',
                pt: '16px',
                pb: '8px'
              }}
            >
              <Typography
                component="h3"
                ref={headingRef}
                tabIndex={-1}
                id={`filter-heading-${categories[0]}`}
                sx={{ fontSize: '14px', fontWeight: 700 }}
              >
                {headers[categories[0]] || categories[0]}
              </Typography>

              <Button
                type="button"
                onClick={handleClose}
                aria-label= {data.closeAria}
                sx={{ minWidth: 'auto', p: '4px', color: 'inherit', '&.Mui-focusVisible': {
                                outline: '2px solid #1460AA',
                                outlineOffset: '2px'
                              } }}
              >
               <img src="/images/arrows/clear-icon.svg" style={{width: '14px', height: '14px' }} alt="" aria-hidden="true"/>
              </Button>
            </Box>

            {/* Scrollable body: sticky headings stack below the fixed header */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: '16px',
                mr: '24px',
                // Slim scrollbar without native arrow buttons.
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-button': {
                  display: 'none',
                  width: 0,
                  height: 0
                },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.35)',
                  borderRadius: '999px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(0,0,0,0.5)'
                }
              }}
            >
              {categories.map((category, index) => (
                <Box key={category} sx={{ mb: index < categories.length - 1 ? '8px' : 0 }}>
                  {index > 0 ? (
                    <Typography
                      component="h3"
                      id={`filter-heading-${category}`}
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        py: '4px',
                        fontSize: '14px',
                        fontWeight: 700
                      }}
                    >
                      {headers[category] || category}
                    </Typography>
                  ) : null}

                  <FormGroup
                    role="list"
                    aria-labelledby={`filter-heading-${category}`}
                  >
                    {options[category].map((option) => (
                      <FormControlLabel
                        key={option}
                        role="listitem"
                        control={
                          <Checkbox
                            checked={
                              tempFilters[category]?.includes(option) || false
                            }
                            onChange={() => handleToggleOption(category, option)}
                            inputProps={{ 'aria-label': getOptionLabel(category, option) }}
                            sx={{
                              '&.Mui-focusVisible': {
                                outline: '2px solid #1460AA',
                                outlineOffset: '2px'
                              }
                            }}
                          />
                        }
                        label={
                          <span style={{ fontSize: '14px' }} aria-hidden="true">
                            {getOptionLabel(category, option)}
                          </span>
                        }
                      />
                    ))}
                  </FormGroup>

                  {index < categories.length - 1 ? (
                    <Divider sx={{ mt: '8px' }} />
                  ) : null}
                </Box>
              ))}
            </Box>

            {/* Fixed footer: action buttons */}
            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '12px', p: '16px' }}>
              <Button type="button" variant="contained" disabled={tempCount === 0} sx={{borderRadius: '24px', textTransform: 'none', 
                fontWeight: 700,
                fontSize: '14px',
                '&.Mui-disabled': {
                  backgroundColor: '#D6D7D9',
                  color: '#6D6D6F',
                },
                '&:focus-Visible': {
                outline: '2px solid #1460AA',
                outlineOffset: '2px',
              } }} onClick={handleApply}>
                {data.applyButton}
              </Button>
              <Button type="button" variant="outlined" disabled={tempCount === 0} sx={{borderRadius: '24px', border: '1px solid #1460AA', textTransform: 'none',
                '&.Mui-disabled': {
                  backgroundColor: '#EFF0F2',
                  color: '#A3A4A6',
                  borderRadius: '24px',
                  border: '1px solid #C9CACC',
                },
                color: '#1460AA',
                fontSize : '14px',
                fontWeight: 700,
                '&:focus-Visible': {
                outline: '2px solid #1460AA',
                outlineOffset: '2px'
              }
              }} onClick={handleReset}>
                {data.resetButton}
              </Button>
            </Box>
          </Paper>
        </TrapFocus>
      </Popper>
    </>
  );
};

const QuarterDetails = ({ year, quarter }: QuarterDetailsProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const releases = t('roadmap_new_releases', {
    returnObjects: true,
    defaultValue: []
  }) as RoadmapRelease[];

  const statuses = t('roadmap_new_statuses', {
    returnObjects: true,
    defaultValue: []
  }) as RoadmapStatus[];

  const quarDetailsData = t('newRoadmap', { returnObjects: true,
    defaultValue: []
  }) as any;

  const bookingFlowImages: { id: number; bookingFlow: string; image: { url: string; alt: string } }[] =
    t('roadmap_new_bookingflowimages', { returnObjects: true, defaultValue: [] }) as any;

  // Filter options and headings come from the translation files
  // (newRoadmap.filterOptions / newRoadmap.filterHeaders).
  const dropdown = quarDetailsData?.filterOptions || {};
  const filterHeaders = quarDetailsData?.filterHeaders || {};

  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, string[]>>(emptyFilters(dropdown));

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Roadmap content comes from the Drupal CMS via i18n.
  const releaseList = useMemo(
    () => (Array.isArray(releases) ? releases : []).map((r) => {
      const raw: any = r;
      // Drupal sends "quarter " (with trailing space); find the value regardless.
      const quarter = Object.keys(raw).reduce(
        (val, k) => (k.trim().toLowerCase() === 'quarter' ? String(raw[k]).trim() : val),
        ''
      );
      const rawFlow = raw.bookingFlow;
      const bookingFlow: string[] = Array.isArray(rawFlow)
        ? rawFlow.map((f: any) => (typeof f === 'string' ? f : f.value))
        : rawFlow ? [rawFlow as string] : [];
      const rawServices = raw.webServices;
      const webServices: string[] = Array.isArray(rawServices)
        ? rawServices.map((s: any) => (typeof s === 'string' ? s : s.value))
        : rawServices ? [rawServices as string] : [];
      const rawSchema = raw.apiSchema ?? raw.api_schema;
      const api_schema: string = Array.isArray(rawSchema)
        ? rawSchema.map((s: any) => (typeof s === 'string' ? s : s.value)).join(', ')
        : rawSchema && typeof rawSchema === 'object'
        ? String(rawSchema.value || '')
        : rawSchema ? String(rawSchema) : '';
      return { ...raw, quarter, bookingFlow, webServices, api_schema };
    }),
    [releases]
  );
  const statusList = Array.isArray(statuses) ? statuses : [];

  const quarterList: string[] = Array.isArray(quarDetailsData?.quarter)
    ? quarDetailsData.quarter
    : [];

  // The quarter comes from the URL and may be in a different language than the
  // one currently selected (e.g. "q1" in the URL while the UI is French, which
  // expects "T1"). Quarters are positional, so resolve by their number (Q1/T1
  // -> 1) and use the current language's label. This keeps the page valid when
  // the user switches languages without re-clicking a tab.
  const quarterNumber = parseInt((quarter || '').replace(/\D/g, ''), 10);
  const quarterLabel =
    quarterNumber >= 1 && quarterNumber <= quarterList.length
      ? quarterList[quarterNumber - 1]
      : (quarter || '').toUpperCase();
  const yearNum = Number(year);
  const isValidYear = year != null && year !== '' && !Number.isNaN(yearNum);
  const isValidQuarter = quarterList.includes(quarterLabel);

  // Scroll to the top whenever this page opens or the year/quarter changes
  // (e.g. after clicking "Read more" on the roadmap overview, or switching
  // quarters). Reset both the window and the inner scroll region (which
  // scrolls independently on desktop).
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [year, quarter]);

  // Keep the URL's quarter segment in sync with the active language. The
  // quarter is positional, so switching language (e.g. EN -> FR) should update
  // the slug from "q1" to "t1" (and vice versa) without changing which quarter
  // is shown. Replaces the history entry so the back button isn't polluted.
  useEffect(() => {
    if (!isValidQuarter) return;
    const desiredSlug = quarterLabel.toLowerCase();
    if (normalize(quarter) !== normalize(desiredSlug)) {
      navigate(`/${i18n.language}/api/roadmap/${year}/${desiredSlug}`, {
        replace: true
      });
    }
  }, [i18n.language, quarterLabel, isValidQuarter, quarter, year, navigate]);

  const items = useMemo(() => {
    if (!isValidYear || !isValidQuarter) return [];
    const filtered = releaseList.filter((r) => {
      if (
        Number(r.year) !== yearNum ||
        normalize(r.quarter) !== normalize(quarterLabel)
      ) {
        return false;
      }

      const webServices = selectedFilters.webservices || [];
      if (
        webServices.length &&
        !(r.webServices || []).some((service) =>
          webServices.some((sel) => normalize(sel) === normalize(service))
        )
      ) {
        return false;
      }

      const bookingFlow = selectedFilters.bookingflow || [];
      if (
        bookingFlow.length &&
        !(r.bookingFlow || []).some((flow) =>
          bookingFlow.some((sel) => bookingFlowKey(sel) === bookingFlowKey(flow))
        )
      ) {
        return false;
      }

      const apiSchema = selectedFilters.api_schema || [];
      if (
        apiSchema.length &&
        !apiSchema.some((sel) => normalize(sel) === normalize(r.api_schema))
      ) {
        return false;
      }

      const status = selectedFilters.status || [];
      if (
        status.length &&
        !status.some((sel) => statusKey(sel) === statusKey(r.status))
      ) {
        return false;
      }

      return true;
    });
    return filtered.slice().sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return da - db;
    });
  }, [
    releaseList,
    isValidYear,
    isValidQuarter,
    yearNum,
    quarterLabel,
    selectedFilters
  ]);

  // A date is only shown when it is a complete calendar date (YYYY-MM-DD).
  // Partial values (e.g. a bare year like "2026") are treated as no date.
  const isCompleteDate = (date?: string) => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    return !Number.isNaN(new Date(`${date}T00:00:00`).getTime());
  };

  // Group items by their formatted date, preserving chronological order.
  const dateLocale = normalize(i18n.language).startsWith('fr') ? 'fr-CA' : 'en-CA';
  const groups = useMemo(() => {
    const map: { date: string; items: RoadmapRelease[] }[] = [];
    items.forEach((item) => {
      const dateLabel = isCompleteDate(item.date)
        ? formatLongDate(item.date, dateLocale)
        : '';
      const existing = map.find((g) => g.date === dateLabel);
      if (existing) {
        existing.items.push(item);
      } else {
        map.push({ date: dateLabel, items: [item] });
      }
    });
    return map;
  }, [items, dateLocale]);

  const fallbackIcon = (status?: string) => {
    const key = statusKey(status);
    if (key === 'achieved') {
      return <CheckIcon aria-hidden="true" style={{fill:"none", stroke: "#20842F"}} className="statusIcon statusIcon--achieved" />;
    }
    if (key === 'progress') {
      return <InprogressIcon style={{fill: "none", stroke: "#CC8800"}} aria-hidden="true" className="statusIcon statusIcon--progress" />;
    }
    return (
      <PlannedIcon aria-hidden="true" className="statusIcon statusIcon--planned" />
    );
  };

  const renderStatusIcon = (status?: string) => fallbackIcon(status);

  const goToQuarter = (q: string) => {
    navigate(`/${i18n.language}/api/roadmap/${year}/${q.toLowerCase()}`);
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Flat list of every applied filter value across all categories, so we can
  // render one removable chip per selection below the toolbar. Chips follow
  // the same order as the `dropdown` lists (categories, then options within
  // each category) rather than the order the user toggled them.
  const appliedChips = Object.keys(dropdown).flatMap((category) => {
    const selected = selectedFilters[category] || [];
    return (dropdown[category] || [])
      .filter((value) => selected.includes(value))
      .map((value) => ({ category, value }));
  });

  const removeFilter = (category: string, value: string) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((v) => v !== value)
    }));
  };

  const chipLabel = (category: string, value: string) => {
    if (category === 'bookingflow') {
      return normalize(value).includes('prime')
        ? quarDetailsData?.primeBooking || 'Prime Booking'
        : quarDetailsData?.postBooking || 'Post Booking';
    }
    if (category === 'status') {
      return quarDetailsData?.statusLabels?.[statusKey(value)] || value;
    }
    return value;
  };

  const chipIcon = (category: string, value: string) => {
    if (category === 'bookingflow') {
      return normalize(value).includes('prime') ? (
        <PrimeBooking className="fltrpillicon"/>
      ) : (
        <PostBooking className="fltrpillicon"/>
      );
    }
    // Show the same status icon as the cards/legend, since statuses are
    // represented by an icon rather than text.
    if (category === 'status') {
      const key = statusKey(value);
      if (key === 'achieved') {
        return <CheckIcon style={{fill: 'transparent', stroke: '#1460AA'}} aria-hidden="true" />;
      }
      if (key === 'progress') {
        return <InprogressIcon style={{fill: 'transparent', stroke: '#1460AA'}} aria-hidden="true" />;
      }
      return <PlannedIcon style={{fill: 'transparent', stroke: '#1460AA'}} aria-hidden="true" />;
    }
    return undefined;
  };

  return (
    <main className="quarterDetails">
      <div className="quarterDetails__header">
        <div className="quarterDetails__header-inner">
          <nav className="quarterDetails__breadcrumb" aria-label="Breadcrumb">
            <button
              type="button"
              className="quarterDetails__breadcrumb-link"
              onClick={() => navigate(`/${i18n.language}/api/roadmap`)}
            >
              {quarDetailsData.roadmap}
            </button>
            <span className="quarterDetails__breadcrumb-sep" aria-hidden="true">
              <img src={"/images/arrows/chevron-right.png"} style={{width: '16px', height: '16px'}} alt="" aria-hidden="true"/>
            </span>
            <span className="quarterDetails__breadcrumb-current">
              {quarterLabel} {year}
            </span>
          </nav>

          <div className="quarterDetails__titleRow">
            <h1 className="quarterDetails__heading">
              {quarterLabel} {year}
            </h1>

            {statusList.length > 0 && (
              <ul className="quarterDetails__legend" aria-label="Status legend">
                {statusList.map((s) => (
                  <li
                    className="quarterDetails__legend-item"
                    key={`legend-${s.id}-${s.status}`}
                  >
                    {renderStatusIcon(s.status)}
                    <span>{s.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="quarterDetails__body">
        <div className="quarterDetails__scroll" ref={scrollRef}>
        <div className="quarterDetails__toolbar">
          <div
            className="quarterDetails__pills"
            role="tablist"
            aria-label="Quarter"
          >
            {quarterList.map((q) => (
              <button
                type="button"
                role="tab"
                key={`pill-${q}`}
                aria-selected={normalize(q) === normalize(quarterLabel)}
                className={`quarterDetails__pill ${
                  normalize(q) === normalize(quarterLabel)
                    ? 'quarterDetails__pill--active'
                    : ''
                }`}
                onClick={() => goToQuarter(q)}
              >
                {q}
              </button>
            ))}
          </div>

          <div className="quarterDetails__filterGroup">
            <FilterDropdown
              options={dropdown}
              headers={filterHeaders}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              data={quarDetailsData}
            />
            {appliedChips.length > 0 && (
              <div className="quarterDetails__chips">
                {appliedChips.map(({ category, value }) => (
                  <Chip
                    key={`${category}-${value}`}
                    icon={chipIcon(category, value)}
                    label={chipLabel(category, value)}
                    onDelete={() => removeFilter(category, value)}
                    deleteIcon={<CloseIcon fontSize="small" />}
                    className="quarterDetails__chip"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        

        <div className="quarterDetails__timeline">
        {groups.map((group) => (
          <div className="quarterDetails__group" key={`group-${group.date}`}>
            {group.date ? (
              <div className="quarterDetails__dateRow">
                <span className="quarterDetails__dot" aria-hidden="true" />
                <h2 className="quarterDetails__date">{group.date}</h2>
              </div>
            ) : null}

            {group.items.map((item) => (
              <article className="quarterDetails__card" key={`item-${item.id}`}>
                {!group.date ? (
                  <span
                    className="quarterDetails__cardDot"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="quarterDetails__card-header">
                  {renderStatusIcon(item.status)}
                  {(() => {
                    const { text, href } = parseTitle(item.title);
                    return (
                      <h3 className="quarterDetails__card-title">
                        {href ? (
                          <a
                            className="quarterDetails__card-title-link"
                            href={href}
                            rel="noopener noreferrer"
                          >
                            {text}
                          </a>
                        ) : (
                          text
                        )}
                      </h3>
                    );
                  })()}
                </div>

                {item.description ? (
                  <p
                    className="quarterDetails__card-desc"
                  >{removePTags(item.description)}</p>
                ) : null}

                {item.bookingFlow && item.bookingFlow.length > 0 && (
                  <div
                    className="quarterDetails__card-flows"
                    role="list"
                    aria-label="Booking flow"
                  >
                    {item.bookingFlow.map((flow) => {
                        const bfImg = Array.isArray(bookingFlowImages)
                          ? bookingFlowImages.find((b) => bookingFlowKey(b.bookingFlow) === bookingFlowKey(flow))
                          : undefined;
                        const bfUrl = bfImg ? getDrupalImageUrl(bfImg.image?.url) : '';
                        return (
                          <span
                            className="quarterDetails__flow"
                            role="listitem"
                            key={`${item.id}-flow-${flow}`}
                          >
                            {bfUrl ? (
                              <img src={bfUrl} alt={bfImg?.image?.alt || ''} aria-hidden="true" className="flowIcon" />
                            ) : bookingFlowKey(flow) === 'prime' ? (
                              <PrimeBooking />
                            ) : (
                              <PostBooking />
                            )}
                            {flow}
                          </span>
                        );
                      })}
                  </div>
                )}

                {item.api_schema ? (
                  <div className="quarterDetails__card-schema">
                    <span className="quarterDetails__schemaChip">
                      {item.api_schema}
                    </span>
                  </div>
                ) : null}

                {item.webServices && item.webServices.length > 0 && (
                  <div
                    className="quarterDetails__card-services"
                    role="list"
                    aria-label="Web services"
                  >
                    {item.webServices.map((service) => (
                      <span
                        className="quarterDetails__serviceChip"
                        role="listitem"
                        key={`${item.id}-svc-${service}`}
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        ))}

         {groups.length === 0 ? (
          <p className="quarterDetails__empty">
            {appliedChips.length > 0
              ? t('newRoadmap.noReleasesFound', { defaultValue: 'No result found' })
              : t('newRoadmap.roadmap_updates_coming_soon', {
                  defaultValue: 'Updates coming soon'
                })}
          </p>
        ) : null}
        </div>

        <button
          type="button"
          className="quarterDetails__backToTop"
          onClick={scrollToTop}
        >
          {quarDetailsData.backToTop}
          <img src="/images/arrows/chevron-up.svg" style={{width: '12px', height: '12px', marginLeft: '5px' }} alt="" aria-hidden="true"/>
        </button>
        </div>
      </div>
    </main>
  );
};

export default QuarterDetails;